import { useCallback, useMemo } from 'react';

import { useLabelCategories } from '../routes/settings/labels/data/labelsRepository';
import type { LabelCategory } from '../routes/settings/labels/types';
import { useLabelsFlags } from '../routes/settings/labels/useLabelsFlags';
import { useContentLabelValueCounts } from '../graphql/contentLabels/queryContentLabelValueCounts';

/**
 * The team's Content Label categories for every surface that is NOT Settings —
 * Performance, Optimize, the Content tab, the Filters drawer.
 *
 * It wraps `labelsRepository.useLabelCategories` rather than the graphql hook: the
 * repository is the documented data seam, it already maps the wire into the
 * `LabelCategory` domain type, and its `loading && !data` keeps the list from
 * blanking during a refetch. This hook only adds the feature flag, and answers in
 * that same `LabelCategory` — a second, structurally identical shape would force a
 * mapper at every call site and drift from the one Settings renders.
 *
 * THE FLAG IS A REAL SKIP, not a filtered answer. `[]` with the request still going
 * out would put `ListContentLabelCategories` on every page of every team, flagged or
 * not.
 *
 * THREE STATES, not two. `useLabelsFlags` derives `v2Enabled` from `useTeamQuery`
 * and reports false while that query is in flight, so `{ enabled: false, loading:
 * false }` would be indistinguishable from "the flag is off" — every consumer would
 * render the flag-off UI and then pop the section in. `loading` therefore stays true
 * until the flag itself is `resolved`.
 */
export type ContentLabelCategoriesResult = {
  /**
   * The categories in SERVER order, MINUS the ones with no values
   */
  categories: LabelCategory[];
  /** The Content Labels v2 flag — distinct from "this team has no categories yet". */
  enabled: boolean;
  /** The flag is not known yet, or an enabled read has nothing to show yet. */
  loading: boolean;
  error?: Error;
  refetch: () => void;
  /** A category by id, `undefined` for a stale or missing one — a URL param cannot crash a menu. */
  categoryById: (labelCategoryId: string | null | undefined) => LabelCategory | undefined;
  /** The server's per-value counts for the given portfolio, so a consumer need not read them twice. */
  valueCounts: ReadonlyMap<string, number>;
};

/**
 * One identity for "no categories", whatever the reason. It becomes a `useMemo`
 * input on the Performance tab's hottest path, so a fresh `[]` per render would
 * invalidate everything downstream on every render.
 */
const NO_CATEGORIES: LabelCategory[] = [];

export function useContentLabelCategories(portfolioId?: number): ContentLabelCategoriesResult {
  const { v2Enabled, resolved } = useLabelsFlags();
  const { data, loading, error, refetch } = useLabelCategories({ skip: !v2Enabled });
  const { counts: valueCounts } = useContentLabelValueCounts({ portfolioId, skip: !v2Enabled || !portfolioId });

  const categories = useMemo(() => {
    if (!v2Enabled || !data?.length) return NO_CATEGORIES;
    // Empty two ways, both leaving the "Unlabeled" bucket as the only row: no values at all, or
    // values the server counts no contents for in THIS portfolio. `=== 0`, so an
    // uncounted value keeps its category — a slow or failed counts read must not blank the menu.
    const withValues = data.filter(
      (category) =>
        category.labels.length > 0 && !category.labels.every((label) => valueCounts.get(label.labelId) === 0),
    );
    return withValues.length ? withValues : NO_CATEGORIES;
  }, [v2Enabled, data, valueCounts]);

  const byId = useMemo(() => new Map(categories.map((category) => [category.labelCategoryId, category])), [categories]);

  const categoryById = useCallback(
    (labelCategoryId: string | null | undefined) => (labelCategoryId == null ? undefined : byId.get(labelCategoryId)),
    [byId],
  );

  return {
    categories,
    enabled: v2Enabled,
    loading: !resolved || (v2Enabled && loading),
    error,
    refetch,
    categoryById,
    valueCounts,
  };
}
